/**
 * Form
 *
 */
import React from 'react'
import { Spin, DatePicker, Select, Input, InputNumber, Row, Col, message, Skeleton, Button, Popconfirm } from 'antd'
import moment from 'moment'
import Api from '../../../Api'
import Exception from '../../../Exception'
import { useSubContratos } from '../Hooks/useSubContratos'
import _ from 'lodash'
import { useMonedas } from '../Hooks/useMonedas'

/**
 *
 * @param {number} id
 * @param {{}} form
 */
export function saveEstimacionFormValues(id, form) {
  const payload = {
    model: 'SubEstimacion',
    id: id || null,
    values: form,
  }

  return new Promise(async (resolve, reject) => {
    try {
      const res = await Api.post('/kernel/mutator', payload)

      if (res.data.ok) {
        resolve(id ? res.data.result : res.data.record)
      } else {
        reject(new Error(res.data.error))
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 *
 * @param {number} id
 * @returns
 */
function fetchEstimacionByPk(id) {
  return Api.query({
    __returnType: 'one',
    model: 'SubEstimacion',
    attributes: [
      'iIdSubContrato',
      'iIdUsuarioSolicita',
      'sFolio',
      'sStatus',
      'dInicio',
      'dTermino',
      'iIdMoneda',
      'dTipoCambio',
      'sComentarios',
    ],
    where: { iId: id },
  })
}

/**
 *
 * @param {{ id?: number, onChange?(): void, onRemoved?(): void, onSave?(): void, fields?: string[] }} props
 * @returns
 */
export function EstimacionForm(props) {
  const [loading, setLoading] = React.useState(false)
  const subcontratos = useSubContratos()
  const monedas = useMonedas()
  const [frm, setFrm] = React.useState({})
  const moneda = monedas.records.find(m => m.iId.toString() === _.get(frm, 'iIdMoneda', '').toString())
  const recordExists = props.id && props.id > 0
  const recordIsPending = (!recordExists)  || (_.get(frm, 'sStatus') === 'PENDIENTE' && _.get(frm, 'iIdUsuarioSolicita') === null)
  const canRevert = _.get(frm, 'sStatus') === 'PENDIENTE' && _.get(frm, 'iIdUsuarioSolicita') !== null

  /**
   *
   */
  const fetch = () => {
    if (!loading) { setLoading(true) }

    fetchEstimacionByPk(props.id)
      .then(r => {
        setLoading(false)
        if (r.data) {
          const newValues = {
            ...r.data,
            iIdSubContrato: _.get(r.data, 'iIdSubContrato', '').toString(),
            iIdMoneda: _.get(r.data, 'iIdMoneda', '').toString(),
          }

          setFrm(newValues)
        } else {
          setFrm({})
          message.warn('No se encontró la estimación')
        }
      })
      .catch(e => {
        setLoading(false)
        Exception.load(e).show()
      })
  }

  /**
   *
   */
  const remove = () => {
    setLoading(true)
    saveEstimacionFormValues(props.id, { iActivo: false })
      .then(() => {
        setLoading(false)
        props.onRemoved && props.onRemoved()
      })
      .catch(e => {
        setLoading(false)
        Exception.load(e).show()
      })
  }

  /**
   *
   * @param {string} key
   * @param {any} val
   */
   const setFormValue = (key, val) => {
    let values = JSON.parse(JSON.stringify(frm))

    if (typeof key === 'string') {
      _.set(values, key, val)
    } else {
      values = {
        ...values,
        ...key,
      }
    }

    setFrm(() => values)
  }

  /**
   *
   * @param {string} key
   * @param {any} defaultValue
   * @returns
   */
  const getFormValue = (key, defaultValue) => {
    return _.get(frm, key, defaultValue)
  }

  /**
   * solicitar
   *
   */
  const solicitar = (aplicar) => {
    setLoading(true)
    saveEstimacionFormValues(props.id, { iIdUsuarioSolicita: aplicar ? +Api.getUserId() : null })
      .then(() => {
        fetch()
      })
      .catch(e => {
        setLoading(false)
        Exception.load(e).show()
      })
  }

  /**
   * onChange(props.id)
   *
   */
  React.useEffect(() => {
    if (props.id) {
      fetch()
    } else {
      setFrm({})
    }

    if (subcontratos.records.length === 0) {
      subcontratos.fetch()
    }

    if (monedas.records.length === 0) {
      monedas.fetch()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id])

  /**
   *
   * @param {*} name
   * @returns
   */
  const canRenderField = (name) => {
    if (!Array.isArray(props.fields)) { return true }
    const idx = props.fields.indexOf(name)
    return idx !== -1
  }

  return (
    <div style={props.style}>
      <Skeleton loading={loading}>
        {/* sub-contrato */}
        {!canRenderField('iIdSubContrato') ? null : (
          <Row style={styles.row}>
            <label>SUB-CONTRATO</label>
            <Select
              showSearch={true}
              allowClear={true}
              style={{ width: '100%' }}
              filterOption={false}
              onChange={(val) => setFormValue('iIdSubContrato', val)}
              onSearch={text => subcontratos.fetch(text)}
              value={getFormValue('iIdSubContrato')}
              notFoundContent={subcontratos.loading ? <Spin size='small' /> : null}
              disabled={!recordIsPending}
              children={subcontratos.records.map((item) =>
                <Select.Option key={item.iId.toString()}>{item.sAlias}</Select.Option>
              )}
            />
          </Row>
        )}

        {/* fechas de inicio y termino */}
        {!canRenderField('dInicio') ? null : (
          <Row style={styles.row}>
            <Col span={11}>
              <label>INICIO DE PERIODO</label>
              <DatePicker
                style={{ width: '100%' }}
                disabled={!recordIsPending}
                value={getFormValue('dInicio') ? moment(getFormValue('dInicio'), 'YYYY-MM-DD') : null}
                onChange={date => date ? setFormValue('dInicio', date.format('YYYY-MM-DD')) : undefined}
              />
            </Col>

            <Col span={2} />

            <Col span={11}>
              <label>TERMINO DE PERIODO</label>
              <DatePicker
                style={{ width: '100%' }}
                disabled={!recordIsPending}
                value={getFormValue('dTermino') ? moment(getFormValue('dTermino'), 'YYYY-MM-DD') : null}
                onChange={date => date ? setFormValue('dTermino', date.format('YYYY-MM-DD')) : undefined}
              />
            </Col>
          </Row>
        )}

        {/* moneda y tipo de cambio */}
        {!canRenderField('iIdMoneda') ? null : (
          <Row style={styles.row}>
            <Col span={11}>
              <label>MONEDA</label>
              <Select
                showSearch={true}
                allowClear={true}
                filterOption={false}
                disabled={!recordIsPending}
                style={{ width: '100%' }}
                onChange={(val) => {
                  if (val) {
                    const sel = monedas.records.find(m => m.iId.toString() === val.toString())

                    setFormValue({
                      iIdMoneda: val,
                      dTipoCambio: sel.iLocal ? 1 : undefined,
                    })
                  } else {
                    setFormValue({ iIdMoneda: undefined, dTipoCambio: undefined })
                  }
                }}
                onSearch={text => subcontratos.fetch(text)}
                value={getFormValue('iIdMoneda')}
                notFoundContent={subcontratos.loading ? <Spin size='small' /> : null}
                children={monedas.records.map((item) =>
                  <Select.Option key={item.iId.toString()}>{item.sNombre}</Select.Option>
                )}
              />
            </Col>

            <Col span={2} />

            <Col span={11}>
              {_.get(moneda, 'iLocal') ? null : (
                <React.Fragment>
                  <label>TIPO DE CAMBIO</label>
                  <InputNumber
                    style={{ width: '100%' }}
                    value={getFormValue('dTipoCambio')}
                    onChange={val => setFormValue('dTipoCambio', val)}
                  />
                </React.Fragment>
              )}
            </Col>
          </Row>
        )}

        {/* comentarios */}
        {!canRenderField('sComentarios') ? null : (
          <Row style={styles.row}>
            <label>COMENTARIOS</label>
            <Input.TextArea
              rows={3}
              value={getFormValue('sComentarios')}
              onChange={e => setFormValue('sComentarios', e.target.value.toUpperCase())}
              disabled={!recordIsPending}
            />
          </Row>
        )}

        {/* acciones */}
        <Row style={{ ...styles.row, display: 'flex', alignItems: 'center' }}>
          {!recordIsPending ? null : (
            <Popconfirm title={recordExists ? 'Guardar cambios' : 'Crear estimación'} onConfirm={() => props.onSave && props.onSave(frm)}>
              <Button style={{ marginRight: 7 }} loading={loading} children='GUARDAR' />
            </Popconfirm>
          )}

          {!(recordExists && (recordIsPending || canRevert)) ? null : (
            <Popconfirm title={canRevert ? 'Si, regresar a pendiente' : 'Si, enviar a pago'} onConfirm={() => solicitar(!canRevert)}>
              <Button type='primary' style={{ marginRight: 7 }} loading={loading} children={canRevert ? 'REGRESAR A PENDIENTE' : 'ENVIAR A PAGO'} />
            </Popconfirm>
          )}

          {!(recordIsPending && recordExists) ? null : (
            <Popconfirm title='¿Eliminar estimación?' onConfirm={() => remove()}>
              <Button type='danger'  loading={loading} disabled={loading} children='ELIMINAR' />
            </Popconfirm>
          )}
        </Row>
      </Skeleton>
    </div>
  )
}

const styles = {
  row: {
    marginBottom: 10,
  }
}
